import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Trophy, ArrowLeft, ChevronRight, ChevronLeft, Crown, Zap, Check } from 'lucide-react';
import { useUserStore } from '../lib/userStore';
import { useLeagueStore } from '../lib/leagueStore';
import { useLanguageStore } from '../lib/languageStore';
import { useThemeStore } from '../lib/themeStore';
import { translate } from '../lib/translations';
import { saveLeagueSettings } from '../lib/api/leagueSettings';
import { sendAdminNewLeagueEmail } from '../lib/api/emailNotifications';
import type { LeagueType } from '../lib/types/leagueMode';

type Step = 1 | 2;

export function CreateLeague() {
  const navigate = useNavigate();
  const username = useUserStore((state) => state.username);
  const setCurrentLeagueId = useUserStore((state) => state.setCurrentLeagueId);
  const setSelectedLeague = useLeagueStore((state) => state.setSelectedLeague);
  const { language } = useLanguageStore();
  const { theme } = useThemeStore();
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [checkingName, setCheckingName] = useState(false);
  const [nameExists, setNameExists] = useState(false);
  const [selectedMode, setSelectedMode] = useState<LeagueType | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    isPrivate: false,
  });

  // Debounce name uniqueness check
  React.useEffect(() => {
    if (!formData.name.trim()) {
      setNameExists(false);
      return;
    }
    setCheckingName(true);
    const timeoutId = setTimeout(async () => {
      try {
        const { data } = await supabase
          .from('leagues')
          .select('league_id')
          .ilike('name', formData.name.trim())
          .maybeSingle();
        setNameExists(!!data);
      } catch {
        setNameExists(false);
      } finally {
        setCheckingName(false);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.name]);

  const canProceedStep1 =
    formData.name.trim().length > 0 && !nameExists && !checkingName;

  const handleSubmit = async () => {
    if (!selectedMode) return;
    setLoading(true);

    try {
      // 1. Resolve user_id
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('user_id')
        .eq('username', username)
        .single();

      if (userError) throw userError;
      if (!userData) throw new Error('User not found');

      // 2. Double-check name uniqueness
      const { data: existingLeague, error: checkError } = await supabase
        .from('leagues')
        .select('league_id')
        .ilike('name', formData.name.trim())
        .maybeSingle();

      if (checkError) throw checkError;
      if (existingLeague) {
        toast.error('Ce nom de ligue est déjà utilisé. Veuillez choisir un autre nom.');
        setLoading(false);
        return;
      }

      // 3. Create the league with the chosen mode
      const { data: leagueData, error: leagueError } = await supabase.rpc('create_league', {
        p_name: formData.name,
        p_is_private: formData.isPrivate,
        p_admin_user_id: userData.user_id,
        p_league_type: selectedMode,
      });

      if (leagueError) throw leagueError;
      if (!leagueData) throw new Error('Failed to create league');

      // 4. Get the new league ID
      const { data: recentLeagueData, error: recentLeagueError } = await supabase
        .rpc('get_most_recent_league_id');

      if (recentLeagueError) throw recentLeagueError;
      if (!recentLeagueData) throw new Error('Failed to get league ID');

      // 5. Add the user to the league
      const { error: joinError } = await supabase.rpc('add_user_to_league', {
        p_user_id: userData.user_id,
        p_league_id: recentLeagueData,
      });

      if (joinError) throw joinError;

      // 6. Save default league settings
      const settingsResult = await saveLeagueSettings(
        recentLeagueData,
        true,        // salary_cap
        88000000,    // max_salary_cap
        12,          // number_forwards
        6,           // number_defensemen
        2,           // number_goalies
        5,           // number_reserves (bench)
        false,       // salary_cap_reserve
        15000000,    // max_salary_cap_reserve
        8,           // number_forwards_protect
        3,           // number_defensemen_protect
        1,           // number_goalies_protect
        10,          // max_demotion
        15,          // max_signing
        false,       // draftMode
        2,           // draftMinPerPick
        true,        // isLineaire
        false,       // isSerpentin
        5,           // injuredReserveLimit
        14,          // daysToComeback
        1,           // ptsGoalAtt
        1,           // ptsAssAtt
        2,           // ptsGoalDef
        1,           // ptsAssDef
        5,           // ptsGoalG
        2,           // ptsWinG
        3,           // ptsShuG
        1            // ptsOtLosseG
      );

      if (settingsResult.error) {
        console.error('Error creating default league settings:', settingsResult.error);
        toast.error('Ligue créée mais erreur lors de la création des paramètres par défaut');
      }

      // 7. Standard Flexible: force require_payment = false
      if (selectedMode === 'standard_flexible') {
        await supabase
          .from('league_settings')
          .update({ require_payment: false })
          .eq('league_id', recentLeagueData);
      }

      // 8. Update store and navigate
      setSelectedLeague({ id: recentLeagueData, name: formData.name });
      setCurrentLeagueId(recentLeagueData);

      // Notify admin (non-blocking)
      try {
        await sendAdminNewLeagueEmail({
          leagueName: formData.name,
          creatorUsername: username || '',
          leagueType: selectedMode,
        });
      } catch (emailErr) {
        console.error('Admin new league email failed (non-blocking):', emailErr);
      }

      toast.success(translate('league.created', language));
      navigate('/settings');
    } catch (error: any) {
      toast.error(translate('failed.create.league', language));
    } finally {
      setLoading(false);
    }
  };

  const isDark = theme === 'dark';
  const cardBg = isDark ? 'rgba(15,23,42,0.95)' : '#fff';

  return (
    <div className="card shadow-lg border-0">
      <div className="card-body p-5">
        <button
          className="btn btn-link text-primary mb-4 p-0 d-flex align-items-center"
          onClick={() => (step === 1 ? navigate('/my-office') : setStep(1))}
        >
          <ArrowLeft size={20} className="me-2" />
          {step === 1 ? translate('back', language) : 'Étape précédente'}
        </button>

        <div className="text-center mb-4">
          <Trophy className="h-12 w-12 text-primary mb-2" />
          <h2 className="card-title text-center mb-1">{translate('create.league.title', language)}</h2>
          <p className="text-muted small">Étape {step} / 2</p>
        </div>

        {/* Progress bar */}
        <div className="progress mb-4" style={{ height: 6 }}>
          <div
            className="progress-bar bg-primary"
            style={{ width: step === 1 ? '50%' : '100%', transition: 'width 0.3s ease' }}
          />
        </div>

        {/* ── Step 1: Name + Privacy ── */}
        {step === 1 && (
          <div>
            <div className="mb-4">
              <label htmlFor="leagueName" className="form-label">
                {translate('league.name', language)}
              </label>
              <input
                type="text"
                className={`form-control ${nameExists ? 'is-invalid' : formData.name && !nameExists ? 'is-valid' : ''}`}
                id="leagueName"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              {checkingName && (
                <div className="form-text text-muted">
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Vérification de la disponibilité du nom...
                </div>
              )}
              {nameExists && (
                <div className="invalid-feedback">
                  Ce nom de ligue est déjà utilisé. Veuillez choisir un autre nom.
                </div>
              )}
              {formData.name && !nameExists && !checkingName && (
                <div className="valid-feedback">Ce nom de ligue est disponible.</div>
              )}
            </div>

            <div className="mb-4 form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="isPrivate"
                checked={formData.isPrivate}
                onChange={(e) => setFormData({ ...formData, isPrivate: e.target.checked })}
              />
              <label className="form-check-label" htmlFor="isPrivate">
                {translate('make.private', language)}
              </label>
              <div className="form-text">{translate('private.description', language)}</div>
            </div>

            <button
              className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
              disabled={!canProceedStep1}
              onClick={() => setStep(2)}
            >
              Suivant : Choisir le mode
              <ChevronRight size={18} />
            </button>
          </div>
        )}

        {/* ── Step 2: Mode selection ── */}
        {step === 2 && (
          <div>
            <p className="text-center text-muted mb-4">
              Choisissez le mode de jeu de votre ligue. Ce choix détermine les règles, les fonctionnalités
              accessibles et la logique de gestion des joueurs.
            </p>

            <div className="row g-3 mb-4">
              {/* Dynasty / Keeper card */}
              <div className="col-12 col-md-6">
                <button
                  type="button"
                  onClick={() => setSelectedMode('dynasty')}
                  className="w-100 h-100 border-0 text-start p-0"
                  style={{ background: 'none', cursor: 'pointer' }}
                >
                  <div
                    className="card h-100"
                    style={{
                      background: cardBg,
                      border: selectedMode === 'dynasty'
                        ? '2px solid #0d6efd'
                        : isDark ? '1px solid rgba(59,130,246,0.2)' : '1px solid #dee2e6',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      boxShadow: selectedMode === 'dynasty' ? '0 0 0 3px rgba(13,110,253,0.15)' : undefined,
                    }}
                  >
                    <div className="card-body p-4">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <div className="rounded-circle d-flex align-items-center justify-content-center"
                          style={{ width: 40, height: 40, background: 'rgba(13,110,253,0.1)' }}>
                          <Crown size={20} className="text-primary" />
                        </div>
                        <div>
                          <h6 className="mb-0 fw-bold">Dynasty / Keeper</h6>
                          <div className="d-flex align-items-center gap-1 flex-wrap mt-1">
                            <span className="badge bg-primary" style={{ fontSize: '0.7rem' }}>Mode classique</span>
                            <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem' }}>5,50 $ / usager</span>
                          </div>
                        </div>
                        {selectedMode === 'dynasty' && (
                          <Check size={20} className="text-primary ms-auto" />
                        )}
                      </div>
                      <ul className="list-unstyled mb-0 small" style={{ color: isDark ? '#94a3b8' : '#6c757d' }}>
                        <li className="d-flex align-items-start gap-2"><span>🔁</span> Pool à vie, multi-saisons</li>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>🔒</span> Joueurs exclusifs par ligue</li>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>⚙️</span> Draft serpentin ou linéaire</li>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>🔄</span> Système d'échanges complet</li>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>🛡️</span> Liste de protection et keeper</li>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>💰</span> Tout est configurable</li>
                        <li className="d-flex align-items-start gap-2"><span>📊</span> Rétention de salaire</li>
                      </ul>
                    </div>
                  </div>
                </button>
              </div>

              {/* Standard Flexible card */}
              <div className="col-12 col-md-6">
                <button
                  type="button"
                  onClick={() => setSelectedMode('standard_flexible')}
                  className="w-100 h-100 border-0 text-start p-0"
                  style={{ background: 'none', cursor: 'pointer' }}
                >
                  <div
                    className="card h-100"
                    style={{
                      background: cardBg,
                      border: selectedMode === 'standard_flexible'
                        ? '2px solid #198754'
                        : isDark ? '1px solid rgba(59,130,246,0.2)' : '1px solid #dee2e6',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      boxShadow: selectedMode === 'standard_flexible' ? '0 0 0 3px rgba(25,135,84,0.15)' : undefined,
                    }}
                  >
                    <div className="card-body p-4">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <div className="rounded-circle d-flex align-items-center justify-content-center"
                          style={{ width: 40, height: 40, background: 'rgba(25,135,84,0.1)' }}>
                          <Zap size={20} className="text-success" />
                        </div>
                        <div>
                          <h6 className="mb-0 fw-bold">Standard Flexible</h6>
                          <span className="badge bg-success" style={{ fontSize: '0.7rem' }}>Nouveau · Gratuit</span>
                        </div>
                        {selectedMode === 'standard_flexible' && (
                          <Check size={20} className="text-success ms-auto" />
                        )}
                      </div>
                      <ul className="list-unstyled mb-0 small" style={{ color: isDark ? '#94a3b8' : '#6c757d' }}>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>🔁</span> Pour une seule saison</li>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>🌐</span> Joueurs partagés entre équipes</li>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>🏗️</span> Construction libre d'équipe</li>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>🚫</span> Pas de draft ni d'échanges</li>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>🆓</span> Limite de signature, système de pointage configurable</li>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>💰</span> Cap salarial, alignement, et bien plus configurable</li>
                        <li className="d-flex align-items-start gap-2"><span>✅</span> Toujours gratuit</li>
                      </ul>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Selected mode summary */}
            {selectedMode && (
              <div className={`alert ${selectedMode === 'dynasty' ? 'alert-primary' : 'alert-success'} d-flex align-items-center gap-2 mb-4`}>
                <Check size={16} />
                <span>
                  Mode sélectionné :{' '}
                  <strong>{selectedMode === 'dynasty' ? 'Dynasty / Keeper' : 'Standard Flexible'}</strong>
                  {selectedMode === 'dynasty' && ' — 5,50 $ par usager, Multi-saisons, Draft et échanges disponibles.'}
                  {selectedMode === 'standard_flexible' && " — toujours gratuit, pas de draft ni d'échanges."}
                </span>
              </div>
            )}

            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={() => setStep(1)}
              >
                <ChevronLeft size={18} />
                Retour
              </button>
              <button
                className={`btn flex-grow-1 d-flex align-items-center justify-content-center gap-2 ${
                  selectedMode === 'dynasty' ? 'btn-primary' : 'btn-success'
                }`}
                disabled={!selectedMode || loading}
                onClick={handleSubmit}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    Création en cours...
                  </>
                ) : (
                  <>
                    {translate('create.league.title', language)}
                    <ChevronRight size={18} />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}